import { useState, useEffect, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { find, isNil } from 'lodash'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ResponsiveContainer, ComposedChart, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Line } from 'recharts'
import {
  CurrencyDollarIcon,
  ReceiptTaxIcon,
  TruckIcon,
  ClockIcon,
  CollectionIcon,
  ScaleIcon,
  ChartPieIcon,
  ChartSquareBarIcon,
  OfficeBuildingIcon,
  TrendingUpIcon,
  SparklesIcon,
  DocumentDownloadIcon,
} from '@heroicons/react/outline'
import Layout from 'containers/Layout'
import PageContent from '../../components/templates/PageContent'
import Alert from '../../components/atoms/Alert'
import apiGet from '../../lib/network/apiGet'
import { formatPrice } from '../../lib/utils'

const PRIMARY = '#fe5579'
const STATUS_COLORS = {
  paid: '#10b981',
  partialpaid: '#f59e0b',
  pending: '#f59e0b',
  confirmed: '#10b981',
  completed: '#0ea5e9',
  inProgress: '#8b5cf6',
  underReview: '#64748b',
  unpaid: '#f59e0b',
  refund: '#8b5cf6',
  failure: '#ef4444',
}
const PIE_PALETTE = ['#10b981', '#f59e0b', '#0ea5e9', '#8b5cf6', '#ef4444', '#64748b']
const BRANCH_PALETTE = ['#fe5579', '#8b5cf6', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#14b8a6', '#6366f1']

// Quick date-range presets (value = months back, or days for 30d)
const RANGE_PRESETS = [
  { key: '30d', days: 30 },
  { key: '3m', months: 3 },
  { key: '12m', months: 12 },
]

const computeRange = preset => {
  const end = moment().endOf('day')
  const start = preset.days ? moment().subtract(preset.days, 'days').startOf('day') : moment().subtract(preset.months, 'months').startOf('day')
  return { startDate: start.toISOString(), endDate: end.toISOString() }
}

const pct = (value, total) => (total > 0 ? Math.round((value / total) * 1000) / 10 : 0)

const OfficeFinancialReport = () => {
  const { t, i18n } = useTranslation()
  const isRtl = i18n.language === 'ar'

  // Super-company users pick a branch via the global sidebar filter; `apiGet` auto-injects it as
  // `branchId`. The backend then scopes to that branch, to all branches when none is selected, or to
  // the current branch for non-super-company users. We re-fetch whenever the global filter changes.
  const companyBranchFilterId = useSelector(state => state?.settings?.companyBranchFilterId)

  const [activePreset, setActivePreset] = useState('12m')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const omr = t('common.omr')
  const money = useCallback(value => `${formatPrice(value, true)} ${omr}`, [omr])
  const moneyShort = useCallback(
    value => {
      const n = Number(value) || 0
      if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k ${omr}`
      return `${formatPrice(n, true)} ${omr}`
    },
    [omr]
  )
  // Compact, currency-free formatter for chart axis ticks — avoids the " OMR" suffix wrapping
  // to a second line (which overlapped the x-axis labels). The tooltip still shows full currency.
  const axisShort = useCallback(value => {
    const n = Number(value) || 0
    if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`
    return `${Math.round(n)}`
  }, [])

  const fetchReport = useCallback(
    async presetKey => {
      setLoading(true)
      setError('')
      const preset = find(RANGE_PRESETS, ['key', presetKey]) || RANGE_PRESETS[2]
      try {
        const res = await apiGet('company-dashboard/financial-report', computeRange(preset), false)
        if (res.success) {
          setData(res.data)
        } else {
          setError(t('officeFinancialReport.messages.failedToLoad'))
        }
      } catch (err) {
        setError(t('officeFinancialReport.messages.failedToLoad'))
      } finally {
        setLoading(false)
      }
    },
    // companyBranchFilterId is intentionally a dep: it changes the auto-injected branchId scope,
    // so the report re-fetches when a super-company user switches the global branch filter.
    [t, companyBranchFilterId]
  )

  useEffect(() => {
    fetchReport(activePreset)
  }, [activePreset, fetchReport])

  const summary = data?.summary
  const statusLabel = useCallback(key => t(`companyDashboard.status.${key}`, { defaultValue: key }), [t])

  const rangeLabel = t(`officeFinancialReport.ranges.${activePreset}`)

  const kpis = useMemo(() => {
    if (!summary) return []
    const net = summary.netRevenue || 0
    return [
      {
        key: 'paid',
        label: t('officeFinancialReport.kpi.paidBookings'),
        value: summary.paidBookings,
        accent: 'from-rose-500 to-pink-600',
        glow: 'bg-rose-400',
        tint: 'from-rose-50/70',
        bar: '#fb7185',
        icon: CollectionIcon,
        caption: rangeLabel,
      },
      {
        key: 'avg',
        label: t('officeFinancialReport.kpi.averageBookingValue'),
        value: money(summary.averageBookingValue),
        accent: 'from-violet-500 to-purple-600',
        glow: 'bg-violet-400',
        tint: 'from-violet-50/70',
        bar: '#a78bfa',
        icon: ScaleIcon,
        caption: t('officeFinancialReport.labels.perBooking'),
      },
      {
        key: 'vat',
        label: t('officeFinancialReport.kpi.netVat'),
        value: money(summary.netVat),
        accent: 'from-amber-400 to-orange-500',
        glow: 'bg-amber-400',
        tint: 'from-amber-50/70',
        bar: '#f59e0b',
        icon: ReceiptTaxIcon,
        share: pct(summary.netVat, net),
        caption: t('officeFinancialReport.labels.ofRevenue'),
      },
      {
        key: 'delivery',
        label: t('officeFinancialReport.kpi.deliveryRevenue'),
        value: money(summary.deliveryRevenue),
        accent: 'from-sky-400 to-blue-500',
        glow: 'bg-sky-400',
        tint: 'from-sky-50/70',
        bar: '#0ea5e9',
        icon: TruckIcon,
        share: pct(summary.deliveryRevenue, net),
        caption: t('officeFinancialReport.labels.ofRevenue'),
      },
      {
        key: 'pipeline',
        label: t('officeFinancialReport.kpi.pipelineRevenue'),
        value: money(summary.pipelineRevenue),
        accent: 'from-teal-400 to-emerald-500',
        glow: 'bg-teal-400',
        tint: 'from-teal-50/70',
        bar: '#14b8a6',
        icon: ClockIcon,
        caption: t('officeFinancialReport.kpi.pipelineBookings', { count: summary.pipelineBookings }),
      },
    ]
  }, [summary, t, money, rangeLabel])

  const statusChartData = useMemo(
    () =>
      (data?.byBookingStatus || []).map(row => ({ name: statusLabel(row.status), key: row.status, revenue: row.revenue, count: row.count })).sort((a, b) => b.revenue - a.revenue),
    [data, statusLabel]
  )

  const paymentChartData = useMemo(
    () => (data?.byPaymentStatus || []).map(row => ({ name: statusLabel(row.paymentStatus), key: row.paymentStatus, value: row.revenue, count: row.count })),
    [data, statusLabel]
  )

  const trendData = useMemo(() => (data?.monthlyTrend || []).map(row => ({ name: moment(row.period, 'YYYY-MM').format('MMM YY'), revenue: row.revenue, count: row.count })), [data])

  const branchData = useMemo(() => (data?.byBranch || []).filter(b => b.branchName), [data])
  const showBranchPanel = branchData.length > 1

  // Revenue by vehicle make (donut) and car-wise comparison by make + model (table).
  const makeData = useMemo(() => (data?.byVehicleMake || []).filter(m => m.revenue > 0 || m.count > 0), [data])
  const modelData = useMemo(() => (data?.byCarModel || []).filter(m => m.revenue > 0 || m.count > 0), [data])
  const showMakePanel = makeData.length > 0
  const showModelPanel = modelData.length > 0

  const statusTotal = useMemo(() => statusChartData.reduce((s, r) => s + r.revenue, 0), [statusChartData])
  const statusBookings = useMemo(() => statusChartData.reduce((s, r) => s + r.count, 0), [statusChartData])
  const paymentTotal = useMemo(() => paymentChartData.reduce((s, r) => s + r.value, 0), [paymentChartData])
  const branchTotal = useMemo(() => branchData.reduce((s, r) => s + r.revenue, 0), [branchData])
  const makeTotal = useMemo(() => makeData.reduce((s, r) => s + r.revenue, 0), [makeData])
  const modelTotal = useMemo(() => modelData.reduce((s, r) => s + r.revenue, 0), [modelData])

  // Client-side CSV export of the currently-loaded report (no extra deps). A UTF-8 BOM keeps
  // Arabic text and the OMR currency readable in Excel. Revenue is exported as raw numbers so
  // the cells stay usable for further analysis; the currency is noted in the column header.
  const exportCsv = useCallback(() => {
    if (!summary) return
    const esc = v => {
      const s = isNil(v) ? '' : String(v)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const row = (...cells) => cells.map(esc).join(',')
    const revHdr = `${t('officeFinancialReport.charts.revenue')} (${omr})`
    const bkHdr = t('officeFinancialReport.charts.bookings')
    const k = key => t(`officeFinancialReport.${key}`)
    const lines = []

    lines.push(row(k('title')))
    if (data?.range) {
      lines.push(row(k('export.range'), `${moment(data.range.startDate).format('YYYY-MM-DD')} — ${moment(data.range.endDate).format('YYYY-MM-DD')}`))
    }
    lines.push('')

    // Summary
    lines.push(row(k('export.summary')))
    lines.push(row(k('export.metric'), k('export.value')))
    lines.push(row(k('labels.netRealizedRevenue'), summary.netRevenue))
    lines.push(row(k('kpi.netVat'), summary.netVat))
    lines.push(row(k('kpi.deliveryRevenue'), summary.deliveryRevenue))
    lines.push(row(k('kpi.paidBookings'), summary.paidBookings))
    lines.push(row(k('kpi.averageBookingValue'), summary.averageBookingValue))
    lines.push(row(k('kpi.pipelineRevenue'), summary.pipelineRevenue))
    lines.push(row(k('export.pipelineBookings'), summary.pipelineBookings))
    lines.push('')

    const section = (titleKey, headerCols, rows) => {
      if (!rows.length) return
      lines.push(row(titleKey))
      lines.push(row(...headerCols))
      lines.push(...rows.map(r => row(...r)))
      lines.push('')
    }

    section(
      k('charts.monthlyTrend'),
      [k('export.period'), revHdr, bkHdr],
      trendData.map(r => [r.name, r.revenue, r.count])
    )
    section(
      k('charts.byBookingStatus'),
      [k('export.category'), revHdr, bkHdr],
      statusChartData.map(r => [r.name, r.revenue, r.count])
    )
    section(
      k('charts.byPaymentStatus'),
      [k('export.category'), revHdr, bkHdr],
      paymentChartData.map(r => [r.name, r.value, r.count])
    )
    section(
      k('charts.byBranch'),
      [k('export.category'), revHdr, bkHdr],
      branchData.map(r => [r.branchName, r.revenue, r.count])
    )
    section(
      k('charts.byVehicleMake'),
      [k('export.make'), revHdr, bkHdr],
      makeData.map(r => [r.make, r.revenue, r.count])
    )
    section(
      k('charts.byCarModel'),
      [k('export.make'), k('export.model'), revHdr, bkHdr, `${k('export.avg')} (${omr})`],
      modelData.map(r => [r.make, r.model, r.revenue, r.count, r.averageBookingValue])
    )

    const csv = `\uFEFF${lines.join('\n')}`
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${k('export.fileName')}_${moment().format('YYYY-MM-DD')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [summary, data, t, omr, trendData, statusChartData, paymentChartData, branchData, makeData, modelData])

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    return (
      <div className="rounded-xl border border-slate-200/80 bg-white/95 px-3.5 py-2.5 text-xs shadow-xl ring-1 ring-slate-900/5 backdrop-blur">
        {label ? <p className="mb-1.5 font-semibold text-slate-700">{label}</p> : null}
        {payload.map(entry => (
          <p key={entry.dataKey} className="flex items-center gap-2 font-medium text-slate-600">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: entry.color }} />
            {entry.name}: <span className="font-bold text-slate-900">{entry.dataKey === 'count' ? entry.value : money(entry.value)}</span>
          </p>
        ))}
      </div>
    )
  }

  const Panel = ({ title, icon: Icon, children, subtitle, right, tone = 'navy' }) => {
    const iconBg = tone === 'navy' ? 'from-[#0b1f33] to-[#1e4060]' : 'from-[#fe5579] to-[#c81e5b]'
    return (
      <div className="overflow-hidden rounded-2xl border border-white/70 bg-white shadow-[0_4px_24px_-8px_rgba(15,23,42,0.15)] ring-1 ring-slate-900/[0.04]">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className={classNames('flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm', iconBg)}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800">{title}</p>
              {subtitle ? <p className="text-[11px] font-medium text-slate-400">{subtitle}</p> : null}
            </div>
          </div>
          {right}
        </div>
        <div className="p-4 sm:p-5">{children}</div>
      </div>
    )
  }

  // ---- composition / leaderboard rows ----
  const CompositionRow = ({ label, color, amount, count, share }) => (
    <div className="group relative overflow-hidden rounded-xl border border-slate-100 bg-gradient-to-r from-white to-slate-50/40 px-3 py-2.5 transition-all duration-200 hover:border-slate-200/90 hover:shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ background: `${color}1f` }}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
          </span>
          <span className="truncate text-sm font-semibold text-slate-700">{label}</span>
          <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-400">{t('officeFinancialReport.labels.bookings', { count })}</span>
        </div>
        <div className="flex shrink-0 flex-col items-end leading-tight">
          <span className="text-sm font-extrabold text-slate-900">{amount}</span>
          <span className="text-[11px] font-bold" style={{ color }}>
            {share}%
          </span>
        </div>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(share, 2)}%`, background: `linear-gradient(90deg, ${color}, ${color}b3)` }} />
      </div>
    </div>
  )

  const renderHero = () => (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0b1f33] via-[#13314c] to-[#1f456a] p-6 shadow-2xl sm:p-8">
      {/* decorative glows */}
      <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#fe5579]/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-sky-500/20 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.08),transparent_45%)]" />

      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white/80 ring-1 ring-white/20 backdrop-blur">
            <SparklesIcon className="h-3.5 w-3.5" />
            {t('officeFinancialReport.badge')}
          </span>
          <h1 className="mt-3 text-2xl font-black tracking-tight text-white sm:text-3xl">{t('officeFinancialReport.title')}</h1>
          <p className="mt-1 text-sm text-white/60">{t('officeFinancialReport.subtitle')}</p>

          <div className="mt-6">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-white/50">{t('officeFinancialReport.labels.netRealizedRevenue')}</p>
            <p className="mt-1 text-4xl font-black tracking-tight text-white sm:text-5xl">{money(summary?.netRevenue ?? 0)}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-400/15 px-2.5 py-1 text-xs font-semibold text-emerald-200 ring-1 ring-emerald-300/20">
                <CollectionIcon className="h-3.5 w-3.5" />
                {t('officeFinancialReport.labels.fromBookings', { count: summary?.paidBookings ?? 0 })}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 text-xs font-semibold text-white/80 ring-1 ring-white/15">
                <ClockIcon className="h-3.5 w-3.5" />
                {t('officeFinancialReport.labels.pipeline', { amount: money(summary?.pipelineRevenue ?? 0) })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col gap-4 lg:w-[340px]">
          {/* range selector (glassy) + export */}
          <div className="flex items-stretch gap-2">
            <div className="inline-flex flex-1 rounded-xl bg-white/10 p-1 ring-1 ring-white/15 backdrop-blur">
              {RANGE_PRESETS.map(preset => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => setActivePreset(preset.key)}
                  className={classNames(
                    'flex-1 rounded-lg px-3 py-1.5 text-xs font-bold transition',
                    activePreset === preset.key ? 'bg-white text-slate-900 shadow' : 'text-white/70 hover:bg-white/10'
                  )}
                >
                  {t(`officeFinancialReport.ranges.${preset.key}`)}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!summary || summary.paidBookings === 0}
              title={t('officeFinancialReport.buttons.export')}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-white/10 px-3 text-xs font-bold text-white/90 ring-1 ring-white/15 backdrop-blur transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <DocumentDownloadIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{t('officeFinancialReport.buttons.export')}</span>
            </button>
          </div>
          {/* mini sparkline */}
          <div className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/15 backdrop-blur">
            <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-white/50">{t('officeFinancialReport.labels.momentum')}</p>
            <ResponsiveContainer width="100%" height={64}>
              <AreaChart data={trendData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="heroSpark" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fff" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#fff" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="revenue" stroke="#ffffff" strokeWidth={2} fill="url(#heroSpark)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )

  const renderSkeleton = () => (
    <div className="animate-pulse space-y-6">
      <div className="h-56 rounded-3xl bg-slate-200/70" />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-32 rounded-2xl bg-slate-200/70" />
        ))}
      </div>
      <div className="h-80 rounded-2xl bg-slate-200/70" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="h-80 rounded-2xl bg-slate-200/70" />
        <div className="h-80 rounded-2xl bg-slate-200/70" />
      </div>
    </div>
  )

  const renderBody = () => {
    if (loading) return renderSkeleton()
    if (error) return <Alert type="error" message={error} />
    if (!summary || summary.paidBookings === 0) {
      return (
        <div className="space-y-6">
          {renderHero()}
          <div className="flex min-h-[260px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white text-slate-500">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
              <CurrencyDollarIcon className="h-7 w-7 text-slate-300" />
            </div>
            <p className="text-sm font-semibold text-slate-600">{t('officeFinancialReport.messages.noData')}</p>
            <p className="mt-1 text-xs text-slate-400">{rangeLabel}</p>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        {renderHero()}

        {/* KPI cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {kpis.map(kpi => (
            <div
              key={kpi.key}
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/70 bg-white p-5 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.18)] ring-1 ring-slate-900/[0.04] transition-all duration-200 hover:-translate-y-1 hover:shadow-xl"
            >
              {/* soft tint wash + glow + watermark icon */}
              <div className={classNames('pointer-events-none absolute inset-0 bg-gradient-to-br to-transparent opacity-60', kpi.tint)} />
              <div
                className={classNames(
                  'pointer-events-none absolute -right-7 -top-7 h-24 w-24 rounded-full opacity-15 blur-2xl transition-opacity group-hover:opacity-30',
                  kpi.glow
                )}
              />
              <kpi.icon className="pointer-events-none absolute -bottom-3 right-2 h-20 w-20 text-slate-900/[0.03] transition-transform duration-300 group-hover:scale-110" />

              <div className="relative flex items-start justify-between">
                <div className={classNames('flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-md ring-4 ring-white/60', kpi.accent)}>
                  <kpi.icon className="h-5 w-5" />
                </div>
                {kpi.share != null ? (
                  <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-extrabold text-slate-700 shadow-sm ring-1 ring-slate-900/5">{kpi.share}%</span>
                ) : null}
              </div>

              <p className="relative mt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{kpi.label}</p>
              <p className="relative mt-0.5 text-2xl font-extrabold tracking-tight text-slate-900">{kpi.value}</p>

              <div className="relative mt-3 pt-3">
                {kpi.share != null ? (
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(kpi.share, 2)}%`, background: kpi.bar }} />
                  </div>
                ) : null}
                {kpi.caption ? <p className={classNames('truncate text-[11px] font-medium text-slate-400', kpi.share != null && 'mt-1.5')}>{kpi.caption}</p> : null}
              </div>
            </div>
          ))}
        </div>

        {/* Monthly trend */}
        <Panel title={t('officeFinancialReport.charts.monthlyTrend')} subtitle={t('officeFinancialReport.charts.monthlyTrendSub')} icon={TrendingUpIcon} tone="brand">
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={trendData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={PRIMARY} stopOpacity={0.32} />
                  <stop offset="95%" stopColor={PRIMARY} stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} reversed={isRtl} />
              <YAxis
                yAxisId="rev"
                tick={{ fontSize: 12, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={axisShort}
                orientation={isRtl ? 'right' : 'left'}
                width={48}
              />
              <YAxis yAxisId="cnt" hide />
              <Tooltip content={<ChartTooltip />} />
              <Area
                yAxisId="rev"
                type="monotone"
                dataKey="revenue"
                name={t('officeFinancialReport.charts.revenue')}
                stroke={PRIMARY}
                strokeWidth={2.5}
                fill="url(#revGradient)"
                dot={{ r: 3, fill: PRIMARY, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
              />
              <Line
                yAxisId="cnt"
                type="monotone"
                dataKey="count"
                name={t('officeFinancialReport.charts.bookings')}
                stroke="#0ea5e9"
                strokeWidth={2}
                dot={false}
                strokeDasharray="4 3"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Revenue by payment status — donut with center total */}
          <Panel title={t('officeFinancialReport.charts.byPaymentStatus')} icon={ChartPieIcon}>
            <div className="flex flex-col items-center gap-6 sm:flex-row">
              <div className="relative h-[230px] w-full max-w-[250px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      {paymentChartData.map((entry, i) => {
                        const c = STATUS_COLORS[entry.key] || PIE_PALETTE[i % PIE_PALETTE.length]
                        return (
                          <linearGradient key={entry.key} id={`payGrad-${entry.key}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={c} stopOpacity={1} />
                            <stop offset="100%" stopColor={c} stopOpacity={0.6} />
                          </linearGradient>
                        )
                      })}
                    </defs>
                    <Pie
                      data={paymentChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={104}
                      innerRadius={68}
                      paddingAngle={4}
                      cornerRadius={7}
                      stroke="#fff"
                      strokeWidth={2}
                    >
                      {paymentChartData.map((entry, i) => (
                        <Cell key={entry.key} fill={`url(#payGrad-${entry.key})`} stroke={STATUS_COLORS[entry.key] || PIE_PALETTE[i % PIE_PALETTE.length]} strokeOpacity={0.15} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('officeFinancialReport.labels.total')}</span>
                  <span className="px-2 text-center text-lg font-black leading-tight text-slate-900">{moneyShort(paymentTotal)}</span>
                  <span className="mt-0.5 text-[10px] font-medium text-slate-400">{t('officeFinancialReport.labels.bookings', { count: summary.paidBookings })}</span>
                </div>
              </div>
              <div className="w-full flex-1 space-y-2">
                {paymentChartData.map((entry, i) => {
                  const c = STATUS_COLORS[entry.key] || PIE_PALETTE[i % PIE_PALETTE.length]
                  const share = pct(entry.value, paymentTotal)
                  return (
                    <div
                      key={entry.key}
                      className="group flex items-center gap-3 rounded-xl border border-slate-100 bg-gradient-to-r from-white to-slate-50/40 px-3 py-2.5 transition-all duration-200 hover:border-slate-200/90 hover:shadow-sm"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: `${c}1f` }}>
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-slate-700">{entry.name}</span>
                          <span className="shrink-0 text-sm font-extrabold text-slate-900">{money(entry.value)}</span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(share, 2)}%`, background: c }} />
                          </div>
                          <span className="w-10 shrink-0 text-end text-[11px] font-bold" style={{ color: c }}>
                            {share}%
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </Panel>

          {/* Revenue by booking status — composition progress list */}
          <Panel title={t('officeFinancialReport.charts.composition')} icon={ChartSquareBarIcon}>
            {/* summary header */}
            <div className="mb-3 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('officeFinancialReport.labels.totalBookings')}</p>
                <p className="mt-0.5 text-lg font-extrabold text-slate-900">{statusBookings}</p>
              </div>
              <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2.5 text-end">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('officeFinancialReport.labels.totalRevenue')}</p>
                <p className="mt-0.5 text-lg font-extrabold text-slate-900">{money(statusTotal)}</p>
              </div>
            </div>

            {/* stacked overview bar */}
            <div className="mb-4 flex h-3.5 w-full overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-900/[0.04]">
              {statusChartData.map(entry => (
                <div
                  key={entry.key}
                  className="h-full transition-all duration-700"
                  style={{ width: `${pct(entry.revenue, statusTotal)}%`, background: STATUS_COLORS[entry.key] || PRIMARY }}
                  title={`${entry.name} · ${pct(entry.revenue, statusTotal)}%`}
                />
              ))}
            </div>

            <div className="space-y-2">
              {statusChartData.map(entry => (
                <CompositionRow
                  key={entry.key}
                  label={entry.name}
                  color={STATUS_COLORS[entry.key] || PRIMARY}
                  amount={money(entry.revenue)}
                  count={entry.count}
                  share={pct(entry.revenue, statusTotal)}
                />
              ))}
            </div>
          </Panel>
        </div>

        {/* Revenue by vehicle make — donut with center total */}
        {showMakePanel ? (
          <Panel title={t('officeFinancialReport.charts.byVehicleMake')} subtitle={t('officeFinancialReport.charts.byVehicleMakeSub')} icon={ChartPieIcon}>
            <div className="flex flex-col items-center gap-6 sm:flex-row">
              <div className="relative h-[230px] w-full max-w-[250px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      {makeData.map((entry, i) => {
                        const c = PIE_PALETTE[i % PIE_PALETTE.length]
                        return (
                          <linearGradient key={i} id={`makeGrad-${i}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={c} stopOpacity={1} />
                            <stop offset="100%" stopColor={c} stopOpacity={0.6} />
                          </linearGradient>
                        )
                      })}
                    </defs>
                    <Pie
                      data={makeData}
                      dataKey="revenue"
                      nameKey="make"
                      cx="50%"
                      cy="50%"
                      outerRadius={104}
                      innerRadius={68}
                      paddingAngle={4}
                      cornerRadius={7}
                      stroke="#fff"
                      strokeWidth={2}
                    >
                      {makeData.map((entry, i) => (
                        <Cell key={i} fill={`url(#makeGrad-${i})`} stroke={PIE_PALETTE[i % PIE_PALETTE.length]} strokeOpacity={0.15} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('officeFinancialReport.labels.total')}</span>
                  <span className="px-2 text-center text-lg font-black leading-tight text-slate-900">{moneyShort(makeTotal)}</span>
                </div>
              </div>
              <div className="w-full flex-1 space-y-2">
                {makeData.map((entry, i) => (
                  <CompositionRow
                    key={i}
                    label={entry.make}
                    color={PIE_PALETTE[i % PIE_PALETTE.length]}
                    amount={money(entry.revenue)}
                    count={entry.count}
                    share={pct(entry.revenue, makeTotal)}
                  />
                ))}
              </div>
            </div>
          </Panel>
        ) : null}

        {/* Car-wise comparison — ranked table by make + model */}
        {showModelPanel ? (
          <Panel title={t('officeFinancialReport.charts.byCarModel')} subtitle={t('officeFinancialReport.charts.byCarModelSub')} icon={TruckIcon}>
            <div className="space-y-1">
              {modelData.map((m, i) => {
                const share = pct(m.revenue, modelTotal)
                const color = BRANCH_PALETTE[i % BRANCH_PALETTE.length]
                return (
                  <div key={`${m.make}-${m.model}-${i}`} className="flex items-center gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-slate-50">
                    <span
                      className={classNames(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-extrabold',
                        i === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm' : 'bg-slate-100 text-slate-500'
                      )}
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1.5 flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-slate-700">
                          {m.make} {m.model}
                        </span>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="text-sm font-bold text-slate-900">{money(m.revenue)}</span>
                          <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">{share}%</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(share, 2)}%`, background: color }} />
                        </div>
                        <span className="shrink-0 whitespace-nowrap text-end text-[11px] font-medium text-slate-400">
                          {t('officeFinancialReport.labels.bookings', { count: m.count })} · {t('officeFinancialReport.labels.avgPerBooking')} {money(m.averageBookingValue)}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </Panel>
        ) : null}

        {/* Revenue by branch — leaderboard */}
        {showBranchPanel ? (
          <Panel title={t('officeFinancialReport.charts.byBranch')} icon={OfficeBuildingIcon} subtitle={t('officeFinancialReport.charts.byBranchSub')}>
            <div className="space-y-1">
              {branchData.map((b, i) => {
                const share = pct(b.revenue, branchTotal)
                const color = BRANCH_PALETTE[i % BRANCH_PALETTE.length]
                return (
                  <div key={b.branchId} className="flex items-center gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-slate-50">
                    <span
                      className={classNames(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-extrabold',
                        i === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm' : 'bg-slate-100 text-slate-500'
                      )}
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1.5 flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-slate-700">{b.branchName}</span>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="text-sm font-bold text-slate-900">{money(b.revenue)}</span>
                          <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">{share}%</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(share, 2)}%`, background: color }} />
                        </div>
                        <span className="w-16 shrink-0 text-end text-[11px] font-medium text-slate-400">{t('officeFinancialReport.labels.bookings', { count: b.count })}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </Panel>
        ) : null}
      </div>
    )
  }

  return (
    <Layout background="#f1f5f9" title="">
      <PageContent>
        <div dir={isRtl ? 'rtl' : 'ltr'} className="mx-auto max-w-7xl px-3 py-5 sm:px-6 sm:py-7">
          {renderBody()}
        </div>
      </PageContent>
    </Layout>
  )
}

export default OfficeFinancialReport
